import React, { useState } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Scroll, Stack } from '@/components/layout';
import { Checkbox, LabeledDivider, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import {
  AuthBackButton,
  AuthBrandLogo,
  AuthLegalPopup,
  AuthPhoneField,
  AuthPrimaryButton,
  AuthScreenBackground,
  AuthTextField,
} from '@/features/auth/components';
import { SocialAuthButton } from '@/features/auth/components/social-auth-button';
import { useLegalPopup } from '@/features/auth/hooks/use-legal-popup';
import { useSignUp } from '@/features/auth/hooks/use-signup';
import { createStyles, useIsWindowed, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';
import { Platform } from 'react-native';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';

const TERMS_VARIANT = 'Font Guardian App/Subtitle/Subtitle 12 Regular';

export function SignUpScreen() {
  const { scale, isTablet } = useScaling();
  const insets = useSafeAreaInsets();
  const styles = useStyles();

  const {
    firstName,
    onChangeFirstName,
    onBlurFirstName,
    lastName,
    onChangeLastName,
    onBlurLastName,
    phone,
    onChangePhone,
    onBlurPhone,
    email,
    onChangeEmail,
    onBlurEmail,
    password,
    onChangePassword,
    onBlurPassword,
    acceptedTerms,
    onChangeAcceptedTerms,
    errors,
    handleSubmit,
    handleBack,
  } = useSignUp();

  const [secureTextEntry, setSecureTextEntry] = useState(true);
  const isWindowed = useIsWindowed();
  const legal = useLegalPopup();

  const strings = STRINGS.auth.signUp;

  const top = insets.top > 0 ? insets.top + scale(16) : scale(50);

  return (
    <AuthScreenBackground scrollable={false}>
      {/* Back */}
      <Stack width={isTablet ? '100%' : scale(335)}>
        <Stack
          left={isTablet ? scale(50) : undefined}
          paddingTop={top}
          style={isTablet ? styles.backButtonTabletContainer : styles.backButtonContainer}>
          <AuthBackButton onPress={handleBack} />
        </Stack>
      </Stack>
      <Stack
        width={scale({ phone: 335, tablet: 380 })}
        marginBottom={scale({ phone: 24, tablet: 32 })}
        style={{
          paddingTop: isTablet ? top + scale(18) : undefined,
        }}>
        {/* Logo */}
        <Stack align="center" marginTop={scale({ phone: 8, tablet: 0 })}>
          <AuthBrandLogo portrait width={scale(154)} height={scale(84)} />
        </Stack>
      </Stack>
      <KeyboardAvoidingView
        behavior={isWindowed ? undefined : 'padding'}
        style={styles.keyboardAvoidingView}>
        <Scroll showsVerticalScrollIndicator={false}>
          <Stack
            flex={1}
            width={scale({ phone: 335, tablet: 380 })}
            style={{
              paddingBottom: insets.bottom + scale(12),
            }}>
            {/* Title */}
            <Text
              variant={
                isTablet
                  ? 'Font Guardian App/Heading/Heading H5 24px'
                  : 'Font Guardian App/Heading/Heading H6 18px'
              }
              style={styles.title}
              accessibilityRole="header">
              {strings.title}
            </Text>

            {/* Subtitle */}
            <Text variant="Font Guardian App/Subtitle/Subtitle 14 Regular" style={styles.subtitle}>
              {strings.subtitle}
            </Text>

            {/* Form */}
            <Stack marginTop={scale(12)} gap={scale(12)}>
              {/* First + Last name */}
              <Stack direction="row" gap={scale(12)}>
                <Stack flex={1}>
                  <AuthTextField
                    label={strings.labelFirstName}
                    value={firstName}
                    onChangeText={onChangeFirstName}
                    onBlur={onBlurFirstName}
                    required
                    error={errors.firstName?.message}
                    placeholder={strings.placeholderFirstName}
                    fieldProps={{
                      autoCapitalize: 'words',
                      autoComplete: 'given-name',
                      maxLength: 100,
                    }}
                  />
                </Stack>
                <Stack flex={1}>
                  <AuthTextField
                    label={strings.labelLastName}
                    value={lastName}
                    onChangeText={onChangeLastName}
                    onBlur={onBlurLastName}
                    required
                    error={errors.lastName?.message}
                    placeholder={strings.placeholderLastName}
                    fieldProps={{
                      autoCapitalize: 'words',
                      autoComplete: 'family-name',
                      maxLength: 100,
                    }}
                  />
                </Stack>
              </Stack>

              {/* Phone */}
              <AuthPhoneField
                label={strings.labelPhone}
                value={phone}
                onChangeText={onChangePhone}
                onBlur={onBlurPhone}
                required
                error={errors.phone?.message}
                // placeholder={strings.placeholderPhone}
              />

              {/* Email */}
              <AuthTextField
                label={strings.labelEmail}
                value={email}
                onChangeText={onChangeEmail}
                onBlur={onBlurEmail}
                required
                error={errors.email?.message}
                placeholder={strings.placeholderEmail}
                fieldProps={{
                  keyboardType: 'email-address',
                  autoCapitalize: 'none',
                  autoComplete: 'email',
                  textContentType: 'emailAddress',
                  trimOnBlur: true,
                  maxLength: 100,
                }}
              />

              {/* Password */}
              <AuthTextField
                label={strings.labelPassword}
                value={password}
                onChangeText={onChangePassword}
                onBlur={onBlurPassword}
                required
                error={errors.password?.message}
                placeholder={strings.placeholderPassword}
                secureTextEntry={secureTextEntry}
                onSecureTextEntryChange={setSecureTextEntry}
                fieldProps={{ maxLength: 50 }}
              />
            </Stack>

            {/* Terms */}
            <Stack direction="row" align="flex-start" gap={scale(8)} marginTop={scale(12)}>
              <Checkbox
                checked={acceptedTerms}
                onValueChange={onChangeAcceptedTerms}
                error={!!errors.acceptedTerms?.message}
              />
              <Text variant={TERMS_VARIANT} style={styles.terms}>
                {strings.termsPrefix}
                <Text variant={TERMS_VARIANT} style={styles.termsLink} onPress={legal.openTerms}>
                  {strings.termsOfService}
                </Text>
                {strings.termsConjunction}
                <Text variant={TERMS_VARIANT} style={styles.termsLink} onPress={legal.openPrivacy}>
                  {strings.privacyPolicy}
                </Text>
              </Text>
            </Stack>
            {errors.acceptedTerms?.message ? (
              <Text
                variant="Font Guardian App/Subtitle/Subtitle 12 Regular"
                style={styles.termsError}>
                {errors.acceptedTerms.message}
              </Text>
            ) : null}

            {/* Create account */}
            <AuthPrimaryButton onPress={handleSubmit} style={styles.submitButton}>
              {strings.buttonCreateAccount}
            </AuthPrimaryButton>

            {Platform.OS === 'web' && <Stack minHeight={scale(30)} />}

            {/* Or login with */}
            <LabeledDivider
              color={colors['3']}
              textVariant="Font Guardian App/Subtitle/Subtitle 12 Regular"
              textColor={colors['6']}
              style={styles.divider}>
              {strings.orLoginWith}
            </LabeledDivider>

            {/* Social auth */}
            <Stack direction="row" justify="center" gap={scale(16)} marginTop={scale(12)}>
              <SocialAuthButton provider="google" />
              <SocialAuthButton provider="apple" />
            </Stack>
          </Stack>
        </Scroll>
      </KeyboardAvoidingView>

      <AuthLegalPopup visible={legal.visible} document={legal.document} onClose={legal.close} />
    </AuthScreenBackground>
  );
}

const useStyles = createStyles(({ scale }) => ({
  keyboardAvoidingView: {
    flex: 1,
  },
  backButtonContainer: {
    alignSelf: 'flex-start',
  },
  backButtonTabletContainer: {
    position: 'absolute',
  },
  title: {
    color: colors['10'],
  },
  subtitle: {
    color: colors['10'],
  },
  terms: {
    flex: 1,
    color: colors['10'],
  },
  termsLink: {
    color: guardianColors['Main/Main'],
  },
  termsError: {
    marginTop: scale(4),
    color: colors.Error,
  },
  submitButton: {
    marginTop: scale(24),
  },
  divider: {
    marginTop: scale(12),
  },
}));

export default SignUpScreen;
